'use client';
import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { TaskSubmitButton } from '@/app/components/shared/TaskSubmitButton';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP, type NewbieTaskName, SCENE } from '@/app/constants';
import {
  useAdsDialog,
  useCostCredits,
  useFreeCreditsTasks,
  useGlobalModal,
  useTaskSubmit,
  useTrials,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormParams, TemplateFormParams, Text2AudioFormParams } from '@/app/stores/slices/task-form';
import type { FormTabType, ModelVersion } from '@/app/types';
import { convertForm, getDefaultReportParams, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import type { FormSubmitButtonProps } from './types';
import { convertTemplate, convertText2Audio, isLimitedTrial } from './utils';

export const FormSubmitButton: FC<PropsWithChildren<FormSubmitButtonProps>> = ({
  maxImageCount,
  taskType,
  children,
  onSubmitSuccess,
  onBeforeSubmit,
  onTrialsUpdated,
  isInspireMe = false,
  processing = false,
  disabled = false,
  disabledTip = '',
  className,
  extra,
  displayCredits = true,
  creditsPlacement,
}) => {
  const { updateTrialsAsync } = useTrials();
  const t = useTranslations();
  const pathname = usePathname();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { tryOpenInsufficientCreditsDialog } = useAdsDialog();
  // 首尾帧视为图生视频
  const { fetchFreeCreditTasks, getExpectedNewbieReward } = useFreeCreditsTasks();

  const { modelVersion, formParams, userInfo, creditsInfo, updateFormParams, invalidateUserQuota, isTeamSpace } =
    useStore((state) => {
      let selectedFormParams: FormParams | TemplateFormParams | Text2AudioFormParams;
      let modelVersion: ModelVersion;
      switch (taskType) {
        case 'img2video':
        case 'headtailimg2video':
        case 'multi_frame':
          selectedFormParams = state.img2videoFormParams;
          modelVersion = state.img2videoFormParams.modelVersion;
          break;
        case 'text2video':
          selectedFormParams = state.text2videoFormParams;
          modelVersion = state.text2videoFormParams.modelVersion;
          break;
        case 'character2video':
          selectedFormParams = state.character2videoFormParams;
          modelVersion = state.character2videoFormParams.modelVersion;
          break;
        case 'reference2image':
          selectedFormParams = state.reference2imageFormParams;
          modelVersion = state.reference2imageFormParams.modelVersion;
          break;
        case 'text2image':
          selectedFormParams = state.text2imageFormParams;
          modelVersion = state.text2imageFormParams.modelVersion;
          break;
        case SCENE:
          selectedFormParams = state.sceneFormParams;
          modelVersion = MODEL_VERSION.Q1;
          break;
        case 'text2audio':
          selectedFormParams = state.text2audioFormParams;
          modelVersion = MODEL_VERSION.Q1;
          break;
        default:
          selectedFormParams = state.img2videoFormParams;
          modelVersion = state.img2videoFormParams.modelVersion;
      }
      return {
        modelVersion,
        formParams: selectedFormParams,
        userInfo: state.userInfo,
        creditsInfo: state.creditsInfo,
        updateFormParams: state.updateFormParams,
        invalidateUserQuota: state.invalidateUserQuota,
        isTeamSpace: state.isTeamSpace,
      };
    });

  const isFree = userInfo?.subs_plan === 'free';
  const templateScene = 'templateScene' in formParams ? formParams.templateScene : undefined;

  //表单提交参数
  const submitValue = useMemo(() => {
    switch (taskType) {
      case 'text2audio':
        return convertText2Audio(formParams as Text2AudioFormParams);
      case SCENE:
        return convertTemplate(formParams as TemplateFormParams);
      default:
        return convertForm({ taskType, formParams: formParams as FormParams, modelVersion, maxImageCount });
    }
  }, [taskType, formParams, modelVersion, maxImageCount]);

  // 判断是否需要消耗体验次数和消耗积分
  const { isInTrials, needCredits } = isLimitedTrial({
    modelVersion,
    isFree,
    settings: submitValue.settings,
    isProMode: submitValue.input.editor_mode === 'professional',
    taskType,
  });
  // use_trial 不代表 0 积分，而是代表是否消耗次数
  //如果消耗次数，需要手动修改 use_trial 的值
  submitValue.settings.use_trial = isInTrials;

  const handleImagePromoCreationShortage = () => {
    // 当检测到免费额度用完时，限制数量为1
    const currentSettings = submitValue.settings;
    if (currentSettings.sample_count && currentSettings.sample_count > 1) {
      updateFormParams(taskType as Exclude<FormTabType, 'scene'>, MODEL_VERSION_SETTINGS_MAP[modelVersion], {
        sample_count: 1,
      });
    }
  };

  const { costCredits, originalCredits } = useCostCredits({
    submitValue,
    templateScene,
    inZeroCreditsTrials: isInTrials && !needCredits,
    onImagePromoCreationShortage: handleImagePromoCreationShortage,
  });

  /**
   * 积分不足
   */
  const creditsInsufficient = useMemo(() => {
    if (
      isFree &&
      (modelVersion === MODEL_VERSION.Q1 || modelVersion === MODEL_VERSION.Q3_A || modelVersion === MODEL_VERSION.Q3_HH)
    )
      return false;
    return !!(costCredits && creditsInfo && creditsInfo.total < costCredits);
  }, [costCredits, creditsInfo, isFree, modelVersion]);

  const getReportParams = (isNewbieTask?: boolean) => {
    return getDefaultReportParams({
      value: submitValue,
      isInspireMe,
      templateScene,
      inspirationId: 'inspirationId' in formParams ? formParams.inspirationId : undefined,
      isNewbieTask,
    });
  };

  // 提交任务及弹窗
  const { submitTask, submitting } = useTaskSubmit({
    taskType,
    templateScene,
    isFree,
    onSubmitSuccess: (data) => {
      // 当日第二次发送弹订阅的逻辑已下沉到 useTaskSubmit 内部，无需在此重复触发
      // 如果消耗免费次数了，需要及时更新剩余次数
      updateTrialsAsync().then(() => {
        onTrialsUpdated?.();
      });
      // 如果提交的是错峰任务，则触发错峰余量重新 fetch
      if (submitValue.settings.schedule_mode === 'nopeak') {
        invalidateUserQuota();
      }

      const matchNexusTask = data.extra_params?.matched_nexus_set;
      if (matchNexusTask) {
        const newbieReward = getExpectedNewbieReward(matchNexusTask as NewbieTaskName);
        if (newbieReward) {
          // 避免和别的toast冲突，延迟1秒再显示
          setTimeout(() => {
            toast({
              type: 'success',
              message: t('newbieTask.completeToast', {
                points: newbieReward,
              }),
            });
          }, 1000);
        }
      }

      fetchFreeCreditTasks();

      const result = onSubmitSuccess?.(data);
      return result;
    },
    templateId: 'templateId' in formParams ? formParams.templateId : undefined,
    getReportParams,
    onImagePromoCreationShortage: handleImagePromoCreationShortage,
  });

  const handleSubmit = async () => {
    if (!userInfo) {
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'create - submit',
        refer_page: pathname,
      });
      return;
    }

    // 如果有 onBeforeSubmit 回调，先执行并检查结果
    if (onBeforeSubmit) {
      try {
        if (!(await onBeforeSubmit())) {
          return;
        }
      } catch {
        return;
      }
    }

    submitTask(submitValue, costCredits);
  };

  // 积分不足时的点击处理逻辑
  const handleCreditsInsufficientClick = () => {
    if (isTeamSpace) {
      openConfirmDialog({
        title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
        description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }

    if (isFree) {
      // 弹订阅弹窗
      const fromPage =
        taskType === 'text2audio'
          ? 'audio_upgrade'
          : `${templateScene ? 'template -' : ''} create button credits insufficient`;
      openPlanDetailDialog({ scene: PlanDialogScene.InsufficientCredits, fromPage });
      reportEvent('vip_price_modal_exposure', { from_page: fromPage });
      sendGAEvent('event', 'subscription_exposure', { from_page: fromPage });
      return;
    }
    // 购买弹窗
    openPurchaseCreditsDialog({
      showTriggerButton: false,
      onClose: () => {
        tryOpenInsufficientCreditsDialog();
      },
    });
    if (taskType === 'text2audio') {
      reportEvent('purchase_credit_exposure', { from_position: 'audio_purchase' });
    }
  };

  const isNonPeakMode = submitValue.settings.schedule_mode === 'nopeak';
  const hideCreditsForFreeUser =
    isFree &&
    (modelVersion === MODEL_VERSION.Q1 || modelVersion === MODEL_VERSION.Q3_A || modelVersion === MODEL_VERSION.Q3_HH);

  return (
    <TaskSubmitButton
      className={className}
      disabled={disabled}
      disabledTip={disabledTip}
      submitting={submitting}
      processing={processing}
      onClick={creditsInsufficient ? handleCreditsInsufficientClick : handleSubmit}
      extra={extra}
      displayCredits={hideCreditsForFreeUser ? false : displayCredits}
      costCredits={costCredits}
      originalCredits={originalCredits}
      creditsInsufficient={creditsInsufficient}
      creditsVariant={isNonPeakMode ? 'submitButton' : 'currentColor'}
      creditsColor={isNonPeakMode ? '#0086FF' : undefined}
      creditsPlacement={creditsPlacement}
    >
      {children}
    </TaskSubmitButton>
  );
};

FormSubmitButton.displayName = 'FormSubmitButton';
